<template>
  <div
    class="vtable-wrapper"
    v-loading="loading"
    :style="{ width: '100%', height: '100%' }"
  >
    <div ref="tableContainer" class="vtable-container"></div>

    <!-- 右键菜单 -->
    <ContextMenu
      :visible="contextMenu.visible"
      :position="contextMenu.position"
      :menu-items="contextMenu.items"
      @menu-click="handleContextMenuClick"
      @hide="hideContextMenu"
    />
  </div>
</template>

<script>
import * as VTable from '@visactor/vtable'
import * as VTablePlugins from '@visactor/vtable-plugins'

import {
  InputEditor,
  TextAreaEditor,
  DateInputEditor,
  ListEditor
} from '@visactor/vtable-editors'
import {
  Group,
  Text,
  Rect,
  createGroup,
  createText,
  createRect
} from '@visactor/vtable/es/vrender'
import { ContextMenuMixin } from './ContextMenuMixin.js'
import ContextMenu from '../ContextMenu/index.vue'
import { dfsFn } from '@/utils/index'

export default {
  name: 'VTable',
  mixins: [ContextMenuMixin],
  components: {
    ContextMenu
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    // 列配置数组
    columns: {
      type: Array,
      required: true,
      validator: value => {
        return Array.isArray(value) && value.length > 0
      }
    },
    // 表格数据
    data: {
      type: Array,
      default: () => [],
      validator: value => {
        return Array.isArray(value)
      }
    },
    // 是否启用列宽自适应
    autoWidth: {
      type: Boolean,
      default: false
    },
    // 是否自动填充容器宽度
    fillWidth: {
      type: Boolean,
      default: true
    },
    // 自动计算列宽的最大值限制
    maxAutoWidth: {
      type: Number,
      default: 300
    },
    // 行高
    rowHeight: {
      type: Number,
      default: 28
    },
    // 默认列宽
    defaultColumnWidth: {
      type: Number,
      default: 90
    },
    // 是否显示序号列
    showIndex: {
      type: Boolean,
      default: false
    },
    // 是否显示勾选框
    showCheckbox: {
      type: Boolean,
      default: false
    },
    // 是否允许多选单元格
    allowMultiSelect: {
      type: Boolean,
      default: true
    },
    // 冻结底部行数
    bottomFrozenRowCount: {
      type: Number,
      default: 0
    },
    frozenColCount: {
      type: Number,
      default: 0
    },
    rightFrozenColCount: {
      type: Number,
      default: 0
    },
    dataConfig: {
      type: Object,
      default: null
    },
    excelFileName: {
      type: String,
      default: '表格'
    },
    tableCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      tableInstance: null,
      selectedRows: [],
      cellStates: new Map(),
      editingCell: null // 当前正在编辑的单元格
    }
  },

  computed: {
    columnsFlat() {
      const arr = []
      dfsFn(
        this.columns,
        item => !item.columns?.length && arr.push(item),
        'columns'
      )
      return arr
    }
  },
  methods: {
    // 设置单元格状态
    setCellState(row, field, state) {
      const key = `${row}-${field}`
      this.cellStates.set(key, state)
    },

    // 处理字段计算
    handleFieldCalculation(changedField, changedValue, record, row, col) {
      // 查找有计算规则的列
      this.columnsFlat.forEach(column => {
        if (column.calculation && column.calculation.dependsOn) {
          const { dependsOn, formula, targetField } = column.calculation

          // 检查当前改变的字段是否在依赖字段中
          if (dependsOn.includes(changedField)) {
            // 获取所有依赖字段的值
            const values = {}
            let allValuesValid = true

            dependsOn.forEach(field => {
              const value = record[field]
              if (value === undefined || value === null) {
                // allValuesValid = false
              } else {
                values[field] = Number(value) || 0
              }
            })

            if (allValuesValid) {
              // 执行计算
              let result
              if (typeof formula === 'function') {
                // 函数式计算
                result = formula(
                  values,
                  record,
                  changedField,
                  this.evaluateFormula
                )
              } else if (typeof formula === 'string') {
                // 字符串表达式计算（支持简单的数学运算）
                result = this.evaluateFormula(formula, values)
              }

              if (result !== undefined) {
                // 更新目标字段
                const targetFieldName = targetField || column.field
                record[targetFieldName] = result

                // 找到目标列的列索引并更新表格显示
                const targetColumnIndex = this.getColumnIndex(targetFieldName)
                if (targetColumnIndex !== -1) {
                  this.tableInstance.changeCellValue(
                    targetColumnIndex,
                    row,
                    result
                  )
                }

                // 触发计算完成事件
                this.$emit('field-calculated', {
                  targetField: targetFieldName,
                  result,
                  dependsOn,
                  changedField,
                  record,
                  row
                })
              }
            }
          }
        }
      })
    },

    // 计算字符串表达式（支持基本的数学运算）
    evaluateFormula(formula, values) {
      try {
        // 替换公式中的字段名为实际值
        let expression = formula
        Object.keys(values).forEach(field => {
          const regex = new RegExp(`\\b${field}\\b`, 'g')
          expression = expression.replace(regex, values[field] || 0)
        })

        // 简单的数学表达式计算（安全的eval替代）
        // 只允许数字、基本运算符和括号
        if (/^[0-9+\-*/().\s]+$/.test(expression)) {
          return eval(expression)
        } else {
          console.warn('不安全的计算表达式:', expression)
          return undefined
        }
      } catch (error) {
        console.error('公式计算错误:', error)
        return undefined
      }
    },

    // 获取列索引
    getColumnIndex(fieldName) {
      // 考虑checkbox列和index列的偏移
      let offset = 0
      if (this.showCheckbox) offset++
      if (this.showIndex) offset++

      const columnIndex = this.columnsFlat.findIndex(
        col => col.field === fieldName
      )
      return columnIndex === -1 ? -1 : columnIndex + offset
    },

    // 初始化表格
    initTable() {
      if (!this.$refs.tableContainer) return

      // 构建列配置
      let columns = []

      // 添加勾选框列（如果启用）
      if (this.showCheckbox) {
        columns.push({
          field: '_checkbox',
          title: '',
          width: 40,
          cellType: 'checkbox',
          headerType: 'checkbox',
          style: {
            textAlign: 'center',
            fontSize: 14
          },
          headerStyle: {
            bgColor: '#f2f2f2',
            fontSize: 14,
            color: '#333333',
            fontWeight: 'bold',
            textAlign: 'center'
          }
        })
      }

      // // 添加序号列（如果启用）
      // if (this.showIndex) {
      //   columns.push({
      //     field: '_index',
      //     title: '',
      //     width: 60,
      //     cellType: 'text',
      //     style: {
      //       textAlign: 'center',
      //       fontSize: 14
      //     },
      //     headerStyle: {
      //       bgColor: '#f2f2f2',
      //       fontSize: 14,
      //       color: '#000000',
      //       fontWeight: 'bold',
      //       textAlign: 'center'
      //     },
      //     customRender: (args) => {
      //       return args.row
      //     }
      //   })
      // }

      // 添加用户定义的列
      const userColumns = this.columns.map(e => this.tranColumn(e))

      // 合并所有列配置
      columns = columns.concat(userColumns)

      this.mergeColumnsSettingCache(columns)

      // VTable配置（不再在option中传container，遵循new ListTable(container, option)签名）
      console.log('VTable接收到的data:', this.data)
      console.log(
        'VTable接收到的data长度:',
        this.data ? this.data.length : 'undefined'
      )

      const tableExportPlugin = new VTablePlugins.TableExportPlugin({
        exportExcelOptions: {
          downloadFile: true,
          fileName: this.excelFileName
        },
        exportCsvOptions: {
          downloadFile: true,
          fileName: 'fff'
        }
      })

      const option = {
        plugins: [tableExportPlugin],
        records: this.data,
        columns,
        dataConfig: this.dataConfig || null,
        // autoWrapText: true,
        // heightMode: 'autoHeight',
        tooltip: {
          isShowOverflowTextTooltip: true
        },

        bottomFrozenRowCount: this.bottomFrozenRowCount,
        rightFrozenColCount: this.rightFrozenColCount,
        // 列宽自适应配置
        // widthMode: this.autoWidth ? 'autoWidth' : 'standard', // 根据内容自动调整列宽
        autoFillWidth: this.fillWidth !== false, // 自动填充容器宽度，默认开启
        // limitMaxAutoWidth: this.maxAutoWidth || 300, // 限制自动计算列宽的最大值
        defaultColWidth: this.defaultColumnWidth || 120, // 默认列宽
        keyboardOptions: {
          selectAllOnCtrlA: true,
          copySelected: true,
          pasteValueToCell: true,
          moveEditCellOnArrowKeys: true,
          moveEditCellOnEnter: true
        },
        select: {
          disableSelect: false,
          disableHeaderSelect: !this.showCheckbox // 如果有勾选框则启用表头选择
        },
        frozenColCount:
          (this.showCheckbox && this.showIndex
            ? 2
            : this.showCheckbox || this.showIndex
              ? 1
              : 0) + this.frozenColCount,
        showHeader: true,
        transpose: false,
        defaultRowHeight: this.rowHeight || 28, // 明确设置行高
        defaultHeaderRowHeight: 32, // 明确设置表头行高
        autoRowHeight: false, // 禁用自动行高
        hierarchyIndent: 10, // 树形结构缩进
        hierarchyTextStartAlignment: true, // 文本对齐
        hierarchyExpandLevel: 2, // 树形结构默认展开到第二层
        select: {
          highlightMode: 'row' // 可以配置为'cross' 或者 'row' 或者 'column'
        },
        theme: {
          scrollStyle: {
            width: 10
          },
          selectionStyle: {
            inlineRowBgColor: '#c4edff'
          },
          frameStyle: {
            borderColor: '#cccccc', // 边框颜色设为黑色
            borderBottLineWidth: 0 // 边框宽度
          },
          cellInnerBorder: true, // 显示单元格内边框
          bodyStyle: {
            borderColor: '#cccccc', // 表格内容区域边框颜色
            textAlign: 'left',
            borderLineWidth: 1
          },
          headerStyle: {
            borderColor: '#cccccc', // 表头边框颜色
            textAlign: 'center',
            borderLineWidth: 1
          }
        }
      }
      if (this.showIndex) {
        option.rowSeriesNumber = {
          title: '序号',
          width: 'auto',
          headerStyle: {
            bgColor: '#f2f2f2',
            fontSize: 14,
            fontWeight: 'bold'
          },
          style: { color: '#333', fontSize: 14, textAlign: 'center' }
        }
      }

      console.log('VTable配置:', option)
      // 创建表格实例（标准签名）
      this.tableInstance = new VTable.ListTable(
        this.$refs.tableContainer,
        option
      )
      // 注册编辑器
      this.registerEditors()

      // 绑定事件
      this.bindEvents()
    },

    // 处理列配置
    tranColumn(col) {
      if (col.columns?.length) {
        return {
          ...col,
          columns: col.columns.map(e => this.tranColumn(e)),
          headerStyle: {
            ...col.headerStyle,
            bgColor: '#f2f2f2',
            fontSize: 14,
            color:
              !col.canModify || col.field === 'operation'
                ? '#333333'
                : '#1890ff', // 根据disabled状态设置列头颜色
            fontWeight: 'bold'
          },
          style: { fontSize: 14, ...col.style }
        }
      }

      const column = {
        columns: col.columns,
        field: col.field,
        title: col.title,
        width: col.width,
        aggregation: col.aggregation,
        cellType: col.cellType || 'text',
        headerType: col.headerType || 'text',
        editCellTrigger: col.cellType === 'checkbox',
        sort: col.sort,
        resizable: col.resizable,
        disable: !col.canEdit,
        style: { fontSize: 14, ...col.style },
        tree: col.tree,
        headerStyle: {
          ...col.headerStyle,
          textAlign: col.headerAlign || (col.cellType ? 'left' : 'center'),
          bgColor: '#f2f2f2',
          fontSize: 14,
          color:
            !(col.canModify || col.canEdit) || col.field === 'operation'
              ? '#333333'
              : '#1890ff', // 根据disabled状态设置列头颜色
          fontWeight: 'bold'
        }
      }

      if (col.fieldFormat) {
        column.fieldFormat = col.fieldFormat
      }

      // 对于checkbox类型的列，支持条件禁用（使用VTable官方的disable属性）
      if (col.cellType === 'checkbox' && col.disableCondition) {
        column.disable = (record, row) => {
          return col.disableCondition(col, record, row)
        }
      }

      // 设置编辑器（为所有非操作列设置编辑器）
      if (col.field !== 'operation') {
        if (!col.canEdit || col.cellType === 'checkbox') {
          // 禁用列或checkbox列不设置编辑器
        } else {
          if (col.editor) {
            const editorConfig = col.editor
            if (editorConfig.type === 'select' && editorConfig.options) {
              column.editor = `${col.field}-list-editor`
            } else if (editorConfig.type === 'number') {
              column.editor = `${col.field}-number-editor`
            } else if (editorConfig.type === 'date') {
              column.editor = 'date-input-editor'
            } else if (editorConfig.type === 'textarea') {
              column.editor = 'text-area-editor'
            } else {
              column.editor = 'input-editor'
            }
          } else {
            // 默认使用输入编辑器
            column.editor = 'input-editor'
          }
        }
      }

      // 处理操作列的自定义渲染 - 使用CustomLayout
      if (col.field === 'operation' && col.buttons) {
        column.customLayout = args => {
          const { table, row, col: colIndex, rect } = args
          const { width, height } = rect || { width: 120, height: 40 }

          const record = this.tableInstance?.getRecordByRowCol(col, row)

          // 创建根容器
          const container = new Group({
            width: width,
            height: height,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start'
          })

          // 为每个按钮创建独立的元素
          col.buttons.forEach((btn, index) => {
            // 创建按钮文本
            const buttonText = new Text({
              text: btn.text,
              fontSize: 12,
              fill: btn.customColor ? btn.customColor(record) : '#1890ff',
              textAlign: 'center',
              textBaseline: 'center',
              cursor: 'pointer',
              boundsPadding: [0, 0, 0, 10]
            })

            buttonText.addEventListener('click', e => {
              const { col, row } = e.currentTarget.parent
              const i = this.tableInstance.getBodyIndexByTableIndex(
                col,
                row
              ).row
              const obj = this.tableInstance.getRecordByRowCol(col, row)
              btn.click(i, obj)
            })
            // 添加到容器
            container.add(buttonText)
          })

          return {
            rootContainer: container,
            renderDefault: false
          }
        }
      }

      // 如果列被禁用，设置单元格样式
      // if (!col.canModify) {
      //   column.style = {
      //     ...column.style
      //     // bgColor: '#f2f2f2',
      //     // color: '#c0c4cc',
      //     // cursor: 'not-allowed'
      //   }
      // }

      // 自定义渲染
      if (col.customRender) {
        column.customRender = col.customRender
      }

      // 字段样式
      if (col.fieldStyle) {
        if (!column.style) column.style = {}
        Object.assign(column.style, col.fieldStyle)
      }

      return column
    },

    // 注册编辑器
    registerEditors() {
      if (!this.tableInstance) return
      console.log('开始注册编辑器...')
      try {
        // 创建并注册基础编辑器实例
        const inputEditor = new InputEditor()
        const dateEditor = new DateInputEditor()
        const textAreaEditor = new TextAreaEditor()
        VTable.register.editor('input-editor', inputEditor)
        VTable.register.editor('date-input-editor', dateEditor)
        VTable.register.editor('text-area-editor', textAreaEditor)
        console.log('基础编辑器注册成功')

        // 为每个列配置注册对应的编辑器
        this.columnsFlat.forEach(column => {
          if (column.editor) {
            const editorConfig = column.editor
            if (editorConfig.type === 'select' && editorConfig.options) {
              // 注册下拉选择编辑器
              const values = editorConfig.options.map(
                opt => opt.label || opt.value
              )
              const listEditor = new ListEditor({ values })
              VTable.register.editor(`${column.field}-list-editor`, listEditor)
              console.log(`注册下拉编辑器: ${column.field}-list-editor`)
            } else if (editorConfig.type === 'number') {
              // 数字编辑器复用input编辑器（可在上层做数字校验）
              VTable.register.editor(
                `${column.field}-number-editor`,
                inputEditor
              )
              console.log(`注册数字编辑器: ${column.field}-number-editor`)
            }
          }
        })

        console.log('所有编辑器注册完成')
      } catch (error) {
        console.error('编辑器注册失败:', error)
      }
    },

    /**
     * 获取列配置信息
     * @param {Object} args - 事件参数
     * @returns {Object} 列配置对象
     */
    getColumnConfig(args) {
      // 从组件的columns配置中查找
      const column = this.columnsFlat.find(col => col.field === args.field)

      // 也可以从VTable实例获取列定义
      let vtableColumn = null
      if (this.tableInstance) {
        try {
          vtableColumn = this.tableInstance.getBodyColumnDefine(
            args.col,
            args.row
          )
        } catch (error) {
          console.warn('获取VTable列定义失败:', error)
        }
      }

      return {
        customConfig: column, // 你的自定义列配置
        vtableConfig: vtableColumn, // VTable的列配置
        isDisabled: !column?.canEdit || vtableColumn?.disable,
        field: args.field,
        title: column?.title || vtableColumn?.title
      }
    },

    // 绑定事件
    bindEvents() {
      if (!this.tableInstance) return

      this.tableInstance.on('checkbox_state_change', args => {
        // args包含 {col, row, value, dataValue, checked}
        const res = []
        if (args.originData) {
          args.originData[args.field] = args.checked
          res.push({
            col: args.col,
            row: args.row,
            value: args.checked,
            record: args.originData,
            oldValue: !args.checked
          })

          const column = this.columnsFlat.find(e => e.field === args.field)
          if (!column?.multiple && args.checked) {
            // 单选  且有一个已经设置了勾选 需要把其他的勾选去掉
            const records = this.tableInstance.getAllBodyCells()
            for (const cells of records) {
              if (
                cells[0].originData &&
                cells[0].originData[args.field] &&
                cells[0].originData !== args.originData
              ) {
                cells[0].originData[args.field] = false
                res.push({
                  col: cells[0].col,
                  row: cells[0].row,
                  value: false,
                  record: cells[0].originData,
                  oldValue: true
                })
              }
            }
          }
        } else {
          const records = this.tableInstance.getAllBodyCells()

          for (const cells of records) {
            cells[0].originData[args.field] = args.checked
            res.push({
              col: cells[0].col,
              row: cells[0].row,
              value: args.checked,
              record: cells[0].originData,
              oldValue: !cells[0].checked
            })
          }
        }

        this.$emit('cell-change', res)
      })

      // 单元格编辑事件
      this.tableInstance.on('change_cell_value', args => {
        const { col, row, changedValue, currentValue } = args
        const config = this.tableInstance.getBodyColumnDefine(col)
        const { field } = config

        let value = changedValue
        const column = this.columnsFlat.find(e => e.field === field)

        // 处理数字
        if (column.fieldType === 'number') {
          if (!value && value !== 0) {
            return
          } else if (isNaN(Number(value))) {
            this.tableInstance.changeCellValue(col, row, currentValue)
            return
          } else if (column.scale >= 0) {
            value = Number(value).toFixed(column.scale) + ''
            if (changedValue != value) {
              this.tableInstance.changeCellValue(col, row, value)
            }
          }
        }

        const dataIndex = this.tableInstance.getBodyIndexByTableIndex(
          col,
          row
        ).row
        const record = this.tableInstance.getRecordByRowCol(col, row)
        let isValid = true
        let errorMessage = ''

        if (column && column.validator) {
          const validationResult = column.validator(value, record)
          if (validationResult !== true) {
            isValid = false
            errorMessage = validationResult || '数据验证失败'
          }
        }

        if (!isValid) {
          // 恢复原值
          this.tableInstance.changeCellValue(col, row, currentValue)
          // 通过事件传递错误信息
          this.$emit('cell-edit', {
            field,
            value: currentValue,
            oldValue: currentValue,
            row,
            col,
            record,
            error: errorMessage
          })
          return
        }

        // 数据转换 - 通过列配置的transform函数
        let finalValue = value
        if (column && column.transform) {
          finalValue = column.transform(value, record)
        }

        // 更新数据到记录中
        record[field] = finalValue

        // 处理字段计算 - 根据列配置的计算规则自动计算其他字段
        this.handleFieldCalculation(field, finalValue, record, row, col)

        this.$emit('cell-change', [
          {
            field,
            value: finalValue,
            oldValue: currentValue,
            row,
            col,
            record
          }
        ])
      })

      // 监听双击事件来检测编辑开始
      this.tableInstance.on('dblclick_cell', args => {
        const column = this.findColumn(args)
        if (
          args.field === 'operation' ||
          !column?.canEdit ||
          column?.cellType === 'checkbox'
        ) {
          args.event.preventDefault()
          args.event.stopPropagation()
          this.$emit('cell-dblclick', args)
          // 双击事件
          return
        } else {
          // 设置编辑状态
          this.editingCell = { col: args.col, row: args.row, field: args.field }
          console.log('编辑开始:', this.editingCell)

          // 启动编辑
          this.tableInstance.startEditCell(args.col, args.row)
        }
      })

      // 监听单元格值改变事件来检测编辑结束
      this.tableInstance.on('change_cell_value', args => {
        if (this.editingCell) {
          console.log('编辑结束:', this.editingCell)
          this.editingCell = null
        }
      })

      // 监听选择事件来检测编辑结束（用户点击其他单元格）
      this.tableInstance.on('selected_cell', args => {
        if (this.editingCell) {
          const { col, row } = this.editingCell
          // 如果选择了不同的单元格，说明编辑结束
          if (args.col !== col || args.row !== row) {
            console.log('编辑结束（选择其他单元格）:', this.editingCell)
            this.editingCell = null
          }
        }
      })

      // 选择事件
      this.tableInstance.on('selected_cell', args => {
        console.log('单元格选择事1件:', args)
        this.updateSelectedRows(args)
      })

      this.tableInstance.on('select_all', args => {
        console.log('全选事件2:', args)
        this.updateSelectedRows(args)
      })

      // 点击事件（处理操作按钮）
      // this.tableInstance.on('click_cell', (args) => {
      //   const { field, row } = args
      // })

      // 监听键盘事件
      this.tableInstance.on('keydown', args => {
        console.log('键盘事件:', args)

        // 对于Ctrl+C复制操作，确保不被阻止（特别是Edge浏览器）
        if (args.event.ctrlKey && args.event.key.toLowerCase() === 'c') {
          console.log('检测到Ctrl+C复制操作，确保不被阻止')
          // 不调用preventDefault()，让VTable的内置复制功能正常工作
          return
        }

        // 对于其他Ctrl组合键（如Ctrl+V粘贴、Ctrl+A全选），也不阻止
        if (
          args.event.ctrlKey &&
          ['v', 'a', 'x', 'z', 'y'].includes(args.event.key.toLowerCase())
        ) {
          console.log(
            `检测到Ctrl+${args.event.key.toUpperCase()}操作，交由VTable处理`
          )
          return
        }

        // 如果按下回车键，开始编辑
        if (
          args.event.key === 'Enter' &&
          !args.event.ctrlKey &&
          !args.event.shiftKey
        ) {
          const cellInfo = this.tableInstance.getCellInfo(args.col, args.row)
          if (cellInfo && cellInfo.field !== 'operation') {
            // 检查列是否被禁用
            const column = this.findColumn(args)
            if (column?.canEdit) {
              this.tableInstance.startEditCell(args.col, args.row)
              setTimeout(() => {
                this.focusEditor()
              }, 50)
            } else {
              console.log('该列已禁用编辑:', cellInfo.field)
            }
          }
        } else if (
          args.event.key === 'Backspace' &&
          !args.event.ctrlKey &&
          !args.event.shiftKey &&
          !this.editingCell // 只在非编辑状态下触发删除事件
        ) {
          this.$emit('backspace', args)
        }
      })
      this.tableInstance.on('resize_column_end', ({ colWidths }) => {
        if (!this.tableCode) return
        const columnsMap = {}

        // 从最后一个开始匹配 避免有序号和复选框
        colWidths.forEach((width, index) => {
          const column = this.tableInstance.getBodyColumnDefine(index)
          if (column?.field) columnsMap[column.field] = { width: width }
        })
        localStorage.setItem(
          `${this.tableCode}_setting`,
          JSON.stringify(columnsMap)
        )
      })

      // 双击编辑逻辑已在上面的dblclick_cell事件中处理

      // 监听右键事件
      // this.tableInstance.on('contextmenu_cell', args => {
      //   this.showContextMenu(args)
      // })

      // 监听整个canvas区域的右键事件
      this.bindCanvasContextMenu()
    },

    // 更新选中行
    updateSelectedRows(args) {
      if (!this.tableInstance) return
      const selectedCells = this.tableInstance.getSelectedCellInfos()
      if (!this.allowMultiSelect && selectedCells.length > 1) {
        this.tableInstance.clearSelected()
        setTimeout(() =>
          this.tableInstance.selectCell(
            selectedCells[0][0].col,
            selectedCells[0][0].row
          )
        )
        return
      }
      this.selectedRows = selectedCells
      this.$emit('row-select', selectedCells)
    },

    // 聚焦编辑器
    focusEditor() {
      console.log('开始聚焦编辑器...')

      this.$nextTick(() => {
        // 尝试多种选择器找到编辑器输入框
        const selectors = [
          '.vtable-editor input',
          '.vtable-editor textarea',
          '.vtable-editor select',
          '.vtable-input-editor input',
          '.vtable-textarea-editor textarea',
          '.vtable-select-editor select',
          'input[data-vtable-editor]',
          'textarea[data-vtable-editor]',
          'select[data-vtable-editor]',
          '.vtable .vtable-editor input',
          '.vtable .vtable-editor textarea',
          '.vtable .vtable-editor select',
          '[contenteditable="true"]'
        ]

        console.log('尝试查找编辑器元素...')

        for (const selector of selectors) {
          const editor = document.querySelector(selector)
          if (editor) {
            console.log('找到编辑器元素:', selector, editor)
            try {
              editor.focus()
              if (editor.select && typeof editor.select === 'function') {
                editor.select()
              }
              console.log('编辑器聚焦成功')
              return
            } catch (error) {
              console.error('聚焦编辑器失败:', error)
            }
          }
        }

        console.log('未找到编辑器元素，检查DOM结构...')
        const vtableContainer = document.querySelector('.vtable')
        if (vtableContainer) {
          console.log(
            'VTable容器内容:',
            vtableContainer.innerHTML.substring(0, 500)
          )
        }
      })
    },

    // 公开方法
    /**
     * 设置表格数据
     * @param {Array} data - 新的表格数据
     */
    setRecords(data) {
      if (!Array.isArray(data)) return
      if (this.tableInstance) {
        this.tableInstance.setRecords(data)
      }
    },

    // 找到某一列的配置
    findColumn(args) {
      return this.columnsFlat.find(col => col.field === args.field)
    },

    /**
     * 获取选中的行索引
     * @returns {Array} 选中行的索引数组
     */
    getSelectedRows() {
      return [...this.selectedRows]
    },

    /**
     * 获取选中的行数据
     * @returns {Array} 选中行的数据数组
     */
    getSelectedRecords() {
      return this.selectedRows
        .filter(index => index < this.data.length)
        .map(index => this.data[index])
    },

    /**
     * 清空选中状态
     */
    clearSelection() {
      this.selectedRows = []
      this.$emit('row-select', [])
    },

    /**
     * 刷新表格
     */
    refresh() {
      if (this.tableInstance) {
        this.tableInstance.renderWithRecreateCells()
      }
    },

    /**
     * 获取表格实例
     * @returns {Object} VTable实例
     */
    getTableInstance() {
      return this.tableInstance
    },

    mergeColumnsSettingCache(columns) {
      const cache = localStorage.getItem(`${this.tableCode}_setting`)
      if (!cache) return
      const map = JSON.parse(cache)

      dfsFn(
        columns,
        e => {
          for (const key in map[e.field] || {}) {
            this.$set(e, key, map[e.field][key])
          }
        },
        'columns'
      )
    }
  },

  watch: {
    // 监听数据变化
    data: {
      handler(newData, oldData) {
        if (this.tableInstance && newData) {
          this.tableInstance.setRecords(newData)
        }
      },
      deep: true
    },

    // 监听列配置变化
    columns: {
      handler() {
        // 重新初始化表格
        if (this.tableInstance) {
          this.tableInstance.release()
          this.$nextTick(() => {
            this.initTable()
          })
        }
      },
      deep: true
    }
  },

  // 生命周期
  mounted() {
    this.$nextTick(() => {
      this.initTable()
    })
  },

  beforeDestroy() {
    if (this.tableInstance) {
      this.tableInstance.release()
    }
  }
}
</script>

<style lang="less" scoped>
.vtable-wrapper {
  width: 100%;

  ::v-deep .vtable {
    border: 1px solid #cccccc;

    canvas {
      margin-left: -1px;
      margin-top: -1px;
    }
  }

  .vtable-container {
    width: 100%;
    height: 100%;

    .vtable-actions {
      margin-bottom: 16px;
      display: flex;
      gap: 8px;

      .action-btn {
        padding: 6px 12px;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        background: #fff;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #40a9ff;
          color: #40a9ff;
        }

        &.primary {
          background: #1890ff;
          border-color: #1890ff;
          color: #fff;

          &:hover {
            background: #40a9ff;
            border-color: #40a9ff;
          }
        }

        &.danger {
          background: #ff4d4f;
          border-color: #ff4d4f;
          color: #fff;

          &:hover {
            background: #ff7875;
            border-color: #ff7875;
          }
        }
      }
    }
  }
}

// 操作按钮样式
:deep(.action-btn) {
  padding: 2px 8px;
  margin: 0 2px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #fff;
  color: #606266;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #ecf5ff;
    border-color: #b3d8ff;
    color: #409eff;
  }
}

// VTable编辑器样式优化
:deep(.vtable-editor) {
  z-index: 9999 !important;
  border: 2px solid #1890ff !important;
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;

  input,
  select,
  textarea {
    width: 100% !important;
    height: 100% !important;
    border: 1px solid #dcdfe6 !important;
    border-radius: 4px !important;
    padding: 0 8px !important;
    font-size: 13px !important;
    color: #606266 !important;
    background-color: #fff !important;
    outline: none !important;
    box-sizing: border-box !important;

    &:focus {
      border-color: #409eff !important;
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
    }
  }

  select {
    cursor: pointer !important;
  }
}

// 单元格状态样式
:deep(.vtable) {
  .cell-disabled {
    background-color: #f5f5f5 !important;
    color: #999 !important;
    cursor: not-allowed !important;
  }

  .cell-editable {
    background-color: #f0f9ff !important;
    cursor: pointer !important;

    &:hover {
      background-color: #e6f7ff !important;
    }
  }

  .cell-editing {
    background-color: #f0fff4 !important;
    border: 2px solid #52c41a !important;
    box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2) !important;
  }
}
</style>
